/**
 * キャンペーンの組込テンプレート（システム提供）。
 * Phase 23a-ext-3: 初見ユーザーが「白紙からスタート」で詰まらないように、
 * 5 種類の典型ユースケースを最初から用意。
 *
 * テンプレート選択 → ボード上に elements / connections が初期配置された状態で
 * 新規キャンペーンが作成される（refId は空 = ユーザーが後から既存リソースを紐付け）。
 */
import type { Campaign, CampaignConnection, CampaignElement } from '@goenhub/shared';

export interface CampaignTemplate {
  id: string; // builtin:uc1 等
  name: string;
  description: string;
  goal: string;
  targetSegment: string;
  /** UI 上のサムネイル絵文字 */
  emoji: string;
  /** ボード初期配置 */
  elements: CampaignElement[];
  connections: CampaignConnection[];
}

/** 簡略化された element 生成ヘルパ */
function el(
  id: string,
  type: CampaignElement['type'],
  x: number,
  y: number,
  refLabel?: string,
  extra: Partial<CampaignElement> = {},
): CampaignElement {
  return { id, type, x, y, ...(refLabel ? { refLabel } : {}), ...extra };
}

function conn(
  id: string,
  fromId: string,
  toId: string,
  triggerKind:
    | 'on_complete'
    | 'on_form_submit'
    | 'after_delay'
    | 'on_open'
    | 'on_link_click'
    | 'manual' = 'on_complete',
  delayMs?: number,
  conditionKind:
    | 'always'
    | 'has_tag'
    | 'in_group'
    | 'form_answered'
    | 'not_form_answered' = 'always',
  conditionExtras: Record<string, unknown> = {},
  label?: string,
): CampaignConnection {
  return {
    id,
    fromId,
    toId,
    fromHandle: 'right',
    toHandle: 'left',
    trigger:
      triggerKind === 'after_delay'
        ? { kind: 'after_delay', delayMs: delayMs ?? 0 }
        : { kind: triggerKind },
    ...(conditionKind !== 'always'
      ? {
          condition: {
            kind: conditionKind,
            ...conditionExtras,
          } as CampaignConnection['condition'],
        }
      : {}),
    ...(label ? { label } : {}),
  };
}

const DAY = 24 * 60 * 60 * 1000;

export const CAMPAIGN_TEMPLATES: CampaignTemplate[] = [
  // ─────────────────────────────────────────
  // UC1: 来場予約 → 来場後フォロー
  // ─────────────────────────────────────────
  {
    id: 'builtin:visit-followup',
    name: '来場予約 → 来場後フォロー',
    emoji: '🏠',
    description:
      'LINE で来場フォームを案内し、予約者に確認メール → 前日リマインダー → 来場後お礼メールを自動送信。',
    goal: '来場予約数の最大化と、来場後の継続接点づくり',
    targetSegment: '注文住宅の検討初期層・モデルハウス未訪問者',
    elements: [
      el('e1', 'line_news', 80, 200, '来場のお知らせ'),
      el('e2', 'form', 320, 200, '来場予約フォーム'),
      el('e3', 'mail_schedule', 560, 120, '予約確認メール'),
      el('e4', 'mail_schedule', 800, 200, '前日リマインダー'),
      el('e5', 'mail_schedule', 1040, 280, '来場後お礼メール'),
    ],
    connections: [
      conn('c1', 'e1', 'e2', 'on_complete'),
      conn('c2', 'e2', 'e3', 'on_form_submit'),
      conn('c3', 'e3', 'e4', 'after_delay', 1 * DAY),
      conn('c4', 'e4', 'e5', 'after_delay', 4 * DAY),
    ],
  },

  // ─────────────────────────────────────────
  // UC2: 資料請求 → 教育シーケンス → 商談化
  // ─────────────────────────────────────────
  {
    id: 'builtin:nurture-sequence',
    name: '資料請求 → 教育 → 商談化',
    emoji: '📚',
    description:
      'フォーム経由の資料請求者に、3日/7日/14日間隔で教育メールを配信。商談予約していない人だけに 30 日後リマインダー。',
    goal: '資料請求者の商談化率 5% を狙う',
    targetSegment: '注文住宅の検討中期層',
    elements: [
      el('e1', 'form', 80, 200, '資料請求フォーム'),
      el('e2', 'mail_schedule', 320, 200, '資料 PDF 送付'),
      el('e3', 'mail_schedule', 560, 200, '事例集メール'),
      el('e4', 'mail_schedule', 800, 200, '料金プランメール'),
      el('e5', 'form', 1040, 120, '商談予約フォーム'),
      el('e6', 'mail_schedule', 1040, 280, '商談リマインダー'),
    ],
    connections: [
      conn('c1', 'e1', 'e2', 'on_form_submit'),
      conn('c2', 'e2', 'e3', 'after_delay', 3 * DAY),
      conn('c3', 'e3', 'e4', 'after_delay', 4 * DAY),
      conn('c4', 'e4', 'e5', 'after_delay', 7 * DAY),
      conn(
        'c5',
        'e5',
        'e6',
        'after_delay',
        30 * DAY,
        'not_form_answered',
        { formId: '' },
      ),
    ],
  },

  // ─────────────────────────────────────────
  // UC3: イベント集客 → 当日 → アンケート
  // ─────────────────────────────────────────
  {
    id: 'builtin:event-aftercare',
    name: 'イベント集客 → 当日 → アンケート',
    emoji: '🎟',
    description:
      'LINE でイベント告知 → 申込フォーム → 確認メール → イベント当日 → 翌日アンケート → 未回答者へリマインダー。',
    goal: '参加者満足度の可視化と次回開催への示唆',
    targetSegment: '過去イベント参加者 + LINE 友だち',
    elements: [
      el('e1', 'line_news', 80, 200, 'イベント告知'),
      el('e2', 'form', 320, 200, '参加申込フォーム'),
      el('e3', 'mail_schedule', 560, 200, '参加確認メール'),
      el('e4', 'event', 800, 200, '当日（マイルストーン）'),
      el('e5', 'form', 1040, 120, 'アンケートフォーム'),
      el('e6', 'mail_schedule', 1040, 280, 'アンケート再依頼'),
    ],
    connections: [
      conn('c1', 'e1', 'e2', 'on_complete'),
      conn('c2', 'e2', 'e3', 'on_form_submit'),
      conn('c3', 'e3', 'e4', 'on_complete'),
      conn('c4', 'e4', 'e5', 'after_delay', 1 * DAY),
      conn(
        'c5',
        'e5',
        'e6',
        'after_delay',
        3 * DAY,
        'not_form_answered',
        { formId: '' },
      ),
    ],
  },

  // ─────────────────────────────────────────
  // UC4: 既存顧客への定期配信
  // ─────────────────────────────────────────
  {
    id: 'builtin:newsletter-regular',
    name: '既存顧客への定期ニュースレター',
    emoji: '📨',
    description:
      '配信先グループに月次ニュースレターを送信。開封者には「特別オファー」、リンククリック者には「興味あり」タグ付与（Phase 23e）。',
    goal: '既存顧客の関心度をデータで可視化',
    targetSegment: '既存配信先全員',
    elements: [
      el('e1', 'mail_contacts_group', 80, 200, '配信先グループ'),
      el('e2', 'mail_schedule', 320, 200, '月次ニュースレター'),
      el('e3', 'mail_schedule', 560, 120, '特別オファー（開封者向け）'),
      el('e4', 'note', 560, 280, 'クリック追跡は Phase 23e で実装'),
    ],
    connections: [
      conn('c1', 'e1', 'e2', 'on_complete'),
      conn('c2', 'e2', 'e3', 'on_open'),
    ],
  },

  // ─────────────────────────────────────────
  // UC5: 購入後ナーチャリング
  // ─────────────────────────────────────────
  {
    id: 'builtin:post-purchase',
    name: '購入後ナーチャリング',
    emoji: '🎁',
    description:
      '購入者グループに感謝メール → 7日後使い方ガイド → 30日後レビュー依頼フォーム → 回答者にお礼+クーポン。',
    goal: 'リピート購入率と顧客満足度の向上',
    targetSegment: '直近 30 日以内の購入者',
    elements: [
      el('e1', 'mail_contacts_group', 80, 200, '購入者グループ'),
      el('e2', 'mail_schedule', 320, 200, '購入感謝メール'),
      el('e3', 'mail_schedule', 560, 200, '使い方ガイド'),
      el('e4', 'form', 800, 200, 'レビュー依頼フォーム'),
      el('e5', 'mail_schedule', 1040, 200, 'お礼+クーポン'),
    ],
    connections: [
      conn('c1', 'e1', 'e2', 'on_complete'),
      conn('c2', 'e2', 'e3', 'after_delay', 7 * DAY),
      conn('c3', 'e3', 'e4', 'after_delay', 23 * DAY),
      conn(
        'c4',
        'e4',
        'e5',
        'on_form_submit',
      ),
    ],
  },
];

/** Campaign 型に変換（initialName を上書き可能） */
export function templateToCampaignSeed(
  template: CampaignTemplate,
  overrides: Partial<Pick<Campaign, 'name' | 'createdBy'>> = {},
): Partial<Campaign> {
  return {
    name: overrides.name ?? template.name,
    description: template.description,
    goal: template.goal,
    targetSegment: template.targetSegment,
    status: 'planning',
    elements: template.elements,
    connections: template.connections,
    fromTemplateId: template.id,
    createdBy: overrides.createdBy ?? '',
  };
}
